import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, KeyRound, Mail, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

// Two-step admin sign-in:
//   1. Enter email  → /auth/admin/check-email  → decides create-password vs sign-in
//   2a. First time  → /auth/admin/setup-password  (user chooses their own password)
//   2b. Otherwise   → /auth/admin/email-login
// A hidden legacy path: typing `verda_admin` (no @) falls back to the old
// username/password endpoint so operators are never locked out.
export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState("email"); // "email" | "signin" | "create" | "legacy"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("email"); setPassword(""); setConfirm(""); };

  const submitEmail = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) { toast.error("Enter your admin email"); return; }
    // Legacy username fallback — no @ symbol
    if (!v.includes("@")) { setStep("legacy"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/admin/check-email", { email: v });
      setStep(r.data.has_password ? "signin" : "create");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not verify email";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!password || password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/admin/setup-password", { email: email.trim(), password });
      login(r.data.user);
      toast.success("Password set. Welcome to Verda Admin.");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password");
    } finally { setBusy(false); }
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    if (!password) { toast.error("Enter your password"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/admin/email-login", { email: email.trim(), password });
      login(r.data.user);
      toast.success(`Signed in as ${r.data.user.name || "Admin"}`);
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const submitLegacy = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/admin/login", { username: email.trim(), password });
      login(r.data.user);
      toast.success("Signed in as admin");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#1C2E24] flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-xl font-semibold text-[#1C2E24]">Verda Admin</span>
        </Link>

        <Card className="p-8 rounded-2xl border-[#E5E7E4] shadow-sm">
          {step === "email" && (
            <>
              <h1 className="font-heading text-2xl font-semibold text-[#1C2E24]">Admin sign-in</h1>
              <p className="text-sm text-[#5C6D63] mt-2">Restricted access. All actions are audit-logged.</p>
              <form onSubmit={submitEmail} className="mt-6 space-y-4" data-testid="admin-login-form">
                <div>
                  <Label htmlFor="ae">Admin email</Label>
                  <div className="mt-2 relative">
                    <Mail className="w-4 h-4 text-[#7D8F84] absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input id="ae" type="text" value={email} onChange={(e) => setEmail(e.target.value)}
                           placeholder="you@example.com" autoComplete="username" data-testid="admin-email"
                           className="h-11 rounded-xl bg-[#F9F8F6] pl-10" autoFocus />
                  </div>
                </div>
                <Button type="submit" disabled={busy} data-testid="admin-email-continue"
                        className="w-full h-11 rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14]">
                  {busy ? "Checking…" : "Continue"}
                </Button>
              </form>
            </>
          )}

          {step === "create" && (
            <>
              <BackButton onClick={reset} />
              <h1 className="font-heading text-2xl font-semibold text-[#1C2E24]">Set your admin password</h1>
              <p className="text-sm text-[#5C6D63] mt-2" data-testid="admin-create-hello">
                Welcome, <b className="text-[#1C2E24]">{email}</b>. This is your first sign-in — pick a strong password (min 8 characters). You&apos;ll use it every time from now on.
              </p>
              <form onSubmit={submitCreate} className="mt-6 space-y-4" data-testid="admin-create-form">
                <PasswordField id="np" label="New password" value={password} onChange={setPassword}
                               testId="admin-new-password" autoFocus />
                <PasswordField id="cp" label="Confirm password" value={confirm} onChange={setConfirm}
                               testId="admin-confirm-password" />
                <Button type="submit" disabled={busy} data-testid="admin-create-btn"
                        className="w-full h-11 rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14]">
                  <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Set password & continue"}
                </Button>
              </form>
            </>
          )}

          {step === "signin" && (
            <>
              <BackButton onClick={reset} />
              <h1 className="font-heading text-2xl font-semibold text-[#1C2E24]">Welcome back</h1>
              <p className="text-sm text-[#5C6D63] mt-2">
                Signing in as <b className="text-[#1C2E24]">{email}</b>.
              </p>
              <form onSubmit={submitSignin} className="mt-6 space-y-4" data-testid="admin-signin-form">
                <PasswordField id="ap" label="Password" value={password} onChange={setPassword}
                               testId="admin-password" autoFocus />
                <Button type="submit" disabled={busy} data-testid="admin-signin-btn"
                        className="w-full h-11 rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14]">
                  <Lock className="w-4 h-4 mr-2" /> {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </>
          )}

          {step === "legacy" && (
            <>
              <BackButton onClick={reset} />
              <h1 className="font-heading text-2xl font-semibold text-[#1C2E24]">Operator sign-in</h1>
              <p className="text-sm text-[#5C6D63] mt-2">Legacy username access.</p>
              <form onSubmit={submitLegacy} className="mt-6 space-y-4" data-testid="admin-legacy-form">
                <div>
                  <Label>Username</Label>
                  <Input value={email} disabled className="mt-2 h-11 rounded-xl bg-[#F9F8F6]" />
                </div>
                <PasswordField id="lp" label="Password" value={password} onChange={setPassword}
                               testId="admin-legacy-password" autoFocus />
                <Button type="submit" disabled={busy} data-testid="admin-legacy-btn"
                        className="w-full h-11 rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14]">
                  <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </>
          )}
        </Card>

        <p className="text-[11px] text-[#7D8F84] mt-4 text-center">
          Not an admin? <Link to="/login" className="text-[#4A6750] hover:underline">Sign in with Google</Link>
        </p>
      </div>
    </div>
  );
}

function BackButton({ onClick }) {
  return (
    <button type="button" onClick={onClick}
            className="flex items-center gap-1 text-xs text-[#7D8F84] hover:text-[#1C2E24] mb-3"
            data-testid="admin-back-btn">
      <ArrowLeft className="w-3.5 h-3.5" /> Use a different email
    </button>
  );
}

function PasswordField({ id, label, value, onChange, testId, autoFocus }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-2 relative">
        <Lock className="w-4 h-4 text-[#7D8F84] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input id={id} type="password" value={value} onChange={(e) => onChange(e.target.value)}
               autoComplete="current-password" data-testid={testId}
               className="h-11 rounded-xl bg-[#F9F8F6] pl-10" autoFocus={autoFocus} />
      </div>
    </div>
  );
}
